import { useEffect, useRef, useState, KeyboardEvent } from 'react'
import { EVENT } from '../../constants/event'
import { loadNote, saveNote } from '../../services/notes'
import { supabaseEnabled } from '../../services/supabase'

const SAVE_DEBOUNCE_MS = 600

export function Notes() {
  const editorRef = useRef<HTMLDivElement>(null)
  const saveTimer = useRef<number | undefined>(undefined)
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved'>('idle')
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    let alive = true
    loadNote(EVENT.slug).then((html) => {
      if (!alive || !editorRef.current) return
      editorRef.current.innerHTML = html
      setLoaded(true)
    })
    return () => {
      alive = false
    }
  }, [])

  const scheduleSave = () => {
    if (!editorRef.current) return
    const html = editorRef.current.innerHTML
    setStatus('saving')
    window.clearTimeout(saveTimer.current)
    saveTimer.current = window.setTimeout(async () => {
      await saveNote(EVENT.slug, html)
      setStatus('saved')
      window.setTimeout(() => setStatus('idle'), 1200)
    }, SAVE_DEBOUNCE_MS)
  }

  // Markdown-style shortcuts only — bold/italic/underline come from the native
  // iOS/macOS selection callout bar (long-press / right-click on selected text)
  // and from Cmd-B / Cmd-I / Cmd-U which the browser handles by default.
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === ' ') {
      const sel = window.getSelection()
      if (!sel || !sel.isCollapsed) return
      const node = sel.anchorNode
      if (!node) return
      const text = node.textContent ?? ''
      const offset = sel.anchorOffset
      const before = text.slice(0, offset)
      const trigger =
        before === '-' || before === '*'
          ? 'ul'
          : before === '1.'
            ? 'ol'
            : null

      if (trigger && !insideList(node)) {
        e.preventDefault()
        if (node.nodeType === Node.TEXT_NODE) {
          ;(node as Text).deleteData(0, before.length)
        }
        document.execCommand(
          trigger === 'ul' ? 'insertUnorderedList' : 'insertOrderedList',
          false,
        )
        scheduleSave()
      }
    }

    // Cmd/Ctrl + B / I / U are handled natively — just kick a save afterwards.
    if ((e.metaKey || e.ctrlKey) && ['b', 'i', 'u'].includes(e.key.toLowerCase())) {
      window.setTimeout(scheduleSave, 0)
    }
  }

  return (
    <div className="relative flex h-full flex-col">
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={scheduleSave}
        onKeyDown={handleKeyDown}
        data-placeholder="Start typing… select text to format, “- ” for a bullet"
        className="notes-editor flex-1 overflow-y-auto px-5 pb-12 pt-5 text-[16px] leading-relaxed text-white"
        style={{ paddingBottom: 'max(2.5rem, var(--safe-bottom))' }}
      />

      {/* Unobtrusive status chip — fades in only while saving/just-saved */}
      <SaveBadge status={status} loaded={loaded} supabase={supabaseEnabled} />
    </div>
  )
}

function SaveBadge({
  status,
  loaded,
  supabase,
}: {
  status: 'idle' | 'saving' | 'saved'
  loaded: boolean
  supabase: boolean
}) {
  if (!loaded) {
    return (
      <div className="pointer-events-none absolute right-3 top-3 text-[11px] text-theme-text-muted">
        loading…
      </div>
    )
  }
  if (status === 'idle') {
    return supabase ? null : (
      <div className="pointer-events-none absolute right-3 top-3 text-[11px] text-theme-text-muted opacity-60">
        local only
      </div>
    )
  }
  return (
    <div
      className={
        'pointer-events-none absolute right-3 top-3 text-[11px] transition-opacity ' +
        (status === 'saved' ? 'text-tertiary-500' : 'text-theme-text-muted')
      }
    >
      {status === 'saving' ? 'saving…' : 'saved'}
    </div>
  )
}

function insideList(node: Node | null): boolean {
  let n: Node | null = node
  while (n) {
    if (n.nodeType === Node.ELEMENT_NODE) {
      const tag = (n as HTMLElement).tagName
      if (tag === 'UL' || tag === 'OL' || tag === 'LI') return true
    }
    n = n.parentNode
  }
  return false
}
